import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useState, useCallback, useEffect, useRef } from "react";
import logoEntrelaces from "@/assets/logo-entrelaces.png";
import { Button } from "./ui/button";
import { useWarningPopup } from "@/contexts/WarningPopupContext";

const atendimentos = [
  { to: "/acolhimento-prioritario", label: "Acolhimento Prioritário" },
  { to: "/avaliacao-cirurgia-bariatrica", label: "Avaliação para Cirurgia Bariátrica" },
  { to: "/avaliacao-vasectomia", label: "Avaliação para Vasectomia" },
  { to: "/psicodiagnostico", label: "Psicodiagnóstico" },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAtendOpen, setIsAtendOpen] = useState(false);
  const atendRef = useRef<HTMLDivElement>(null);
  const warningPopup = useWarningPopup();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (atendRef.current && !atendRef.current.contains(e.target as Node)) {
        setIsAtendOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const scrollToSection = useCallback((sectionId: string, path: string) => {
    const scrollToElement = () => {
      const element = document.getElementById(sectionId);
      if (element) {
        const headerHeight = 112;
        const elementPosition = element.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: elementPosition - headerHeight,
          behavior: "smooth",
        });
      }
    };

    if (location.pathname === "/" || location.pathname === path) {
      window.history.pushState({}, "", path);
      scrollToElement();
    } else {
      navigate(path);
      setTimeout(scrollToElement, 100);
    }
    setIsMenuOpen(false);
  }, [location.pathname, navigate]);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-28">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src={logoEntrelaces} 
                alt="Entrelaces Psicologia" 
                className="h-12 md:h-24 w-auto"
                width={95}
                height={80}
                sizes="80px"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <button
                onClick={() => scrollToSection("inicio", "/")}
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                Início
              </button>
              <button
                onClick={() => scrollToSection("sobre", "/sobre")}
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                Sobre
              </button>
              <button
                onClick={() => scrollToSection("servicos", "/servicos")}
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                Serviços
              </button>
              <div className="relative" ref={atendRef}>
                <button
                  onClick={() => setIsAtendOpen((v) => !v)}
                  className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                  aria-expanded={isAtendOpen}
                >
                  Atendimentos
                  <ChevronDown className={`w-4 h-4 transition-transform ${isAtendOpen ? "rotate-180" : ""}`} />
                </button>
                {isAtendOpen && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-72 rounded-xl border border-border bg-background shadow-lg py-2 animate-fade-in">
                    {atendimentos.map((a) => (
                      <Link
                        key={a.to}
                        to={a.to}
                        onClick={() => setIsAtendOpen(false)}
                        className="block px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                      >
                        {a.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => scrollToSection("localizacao", "/localizacao")}
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                Localização
              </button>
            </nav>

            {/* CTA Button */}
            <div className="hidden md:flex items-center">
              <Button variant="hero" size="lg" onClick={warningPopup.open}>
                Agendar pelo WhatsApp
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-foreground"
              aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu de navegação"}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-border animate-fade-in">
              <nav className="flex flex-col gap-4">
                <button
                  onClick={() => scrollToSection("inicio", "/")}
                  className="text-base font-medium text-foreground hover:text-primary transition-colors px-2 py-2 text-left"
                >
                  Início
                </button>
                <button
                  onClick={() => scrollToSection("sobre", "/sobre")}
                  className="text-base font-medium text-foreground hover:text-primary transition-colors px-2 py-2 text-left"
                >
                  Sobre
                </button>
                <button
                  onClick={() => scrollToSection("servicos", "/servicos")}
                  className="text-base font-medium text-foreground hover:text-primary transition-colors px-2 py-2 text-left"
                >
                  Serviços
                </button>
                <div className="px-2 pt-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Atendimentos</p>
                  <div className="flex flex-col gap-1">
                    {atendimentos.map((a) => (
                      <Link
                        key={a.to}
                        to={a.to}
                        onClick={() => setIsMenuOpen(false)}
                        className="text-sm font-medium text-foreground hover:text-primary transition-colors py-1.5"
                      >
                        {a.label}
                      </Link>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => scrollToSection("localizacao", "/localizacao")}
                  className="text-base font-medium text-foreground hover:text-primary transition-colors px-2 py-2 text-left"
                >
                  Localização
                </button>
                <div className="pt-4 border-t border-border">
                  <Button 
                    variant="hero" 
                    size="lg" 
                    className="w-full"
                    onClick={() => {
                      setIsMenuOpen(false);
                      warningPopup.open();
                    }}
                  >
                    Agendar pelo WhatsApp
                  </Button>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
